import React, { useState } from "react";
import { Map, Marker, Overlay } from "pigeon-maps";
import Meter from "../../assets/svg/meter";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Search1 from "../../assets/svg/search1";
import Typography from "@mui/material/Typography";
import Dashboard360MapSearchBar from "../dashboardCards/Dashboard360MapSearchBar";
import { MarkerData } from "../../types";

interface Dashboard360MapProps {
  filteredMarkers: MarkerData[];
  setMarkers: (markers: MarkerData[]) => void;
  setStatus: (status: string) => void;
}

const Dashboard360Map: React.FC<Dashboard360MapProps> = ({
  filteredMarkers,
  setMarkers,
  setStatus,
}) => {
  const [selectedMarker, setSelectedMarker] = useState<MarkerData | null>(null);
  const [zoom, setZoom] = useState<number>(14);
  const defaultCenter: [number, number] = [18.5204, 73.8567];
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isClicked, setIsClicked] = useState<boolean>(false);

  const grayScaleProvider = (
    x: number,
    y: number,
    z: number,
    dpr?: number
  ): string => {
    return `https://cartodb-basemaps-a.global.ssl.fastly.net/light_all/${z}/${x}/${y}@${
      dpr || 1
    }x.png`;
  };

  const handleMarkerClick = (marker: MarkerData): void => {
    setSelectedMarker(marker);
    setZoom(14);
  };

  const zoomIn = (): void =>
    setZoom((prevZoom: number) => Math.min(prevZoom + 1, 18));
  const zoomOut = (): void =>
    setZoom((prevZoom: number) => Math.max(prevZoom - 1, 11));

  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        width: "100%",
      }}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          boxShadow: "inset 0 0 20px rgba(0,0,0,0.5)",
          zIndex: 2,
          pointerEvents: "none", // important: lets you still click the map
        }}
      />
      <Map
        height={480}
        center={
          selectedMarker
            ? [selectedMarker.latitude, selectedMarker.longitude]
            : defaultCenter
        }
        zoom={zoom}
        defaultZoom={14}
        provider={grayScaleProvider}
        attribution={false}>
        {filteredMarkers?.map((marker: MarkerData) => (
          <Marker
            key={marker.meterNo}
            width={30}
            anchor={[marker.latitude, marker.longitude]}
            onClick={() => handleMarkerClick(marker)}>
            <Meter fill={marker.status === "active" ? "#34C759" : "#FF3B30"} />
          </Marker>
        ))}
        {selectedMarker && (
          <Overlay anchor={[selectedMarker.latitude, selectedMarker.longitude]}>
            <div
              style={{
                background: "white",
                padding: "5px",
                borderRadius: "5px",
              }}>
              Meter Number: {selectedMarker.meterNo}
            </div>
          </Overlay>
        )}
      </Map>

      {/* Zoom controls */}
      <Box
        sx={{
          position: "absolute",
          bottom: "40px",
          right: "40px",
          display: "flex",
          flexDirection: "column",
        }}>
        <button
          onClick={zoomIn}
          style={{
            padding: "2px 5px",
            backgroundColor: "white",
            border: "1px solid #5F6368",
            borderRadius: "3px 3px 0 0",
            cursor: "pointer",
          }}>
          +
        </button>
        <button
          onClick={zoomOut}
          style={{
            padding: "2px 5px",
            backgroundColor: "white",
            border: "1px solid #5F6368",
            borderRadius: "0 0 3px 3px",
            cursor: "pointer",
          }}>
          -
        </button>
      </Box>

      <Box
        className="dashboard_map_search_box"
        onMouseEnter={() => {
          if (!isClicked) {
            setIsHovered(true);
          }
        }}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => {
          setIsHovered(false);
          setIsClicked(!isClicked);
          if (!isHovered) {
            setIsHovered(true);
          }
        }}>
        <IconButton
          sx={{
            transition: "color 0.3s",
            zIndex: 111,
          }}>
          <Search1 fill={isHovered || isClicked ? "#1280E7" : "#5F6368"} />
        </IconButton>

        {isHovered && (
          <Typography variant="body2" p={"0 10px 0 15px"} color="#5F6368">
            Enhance search with actionable insights
          </Typography>
        )}
      </Box>

      {isClicked && (
        <Box
          sx={{
            position: "absolute",
            bottom: "40px",
            left: "20px",
            zIndex: 11,
          }}
          className="dashboard_map_search_box">
          <Dashboard360MapSearchBar
            setIsClicked={setIsClicked}
            setMarkers={setMarkers}
            setStatus={setStatus}
          />
        </Box>
      )}
    </div>
  );
};

export default Dashboard360Map;
